<!-- OverlayStack should be present once on the page. It displays all the currently active modals. -->
<template>
    <transition name="fade">
        <div v-if="hasActiveModals"
             class="modl-backdrop-base"></div>
    </transition>

    <template v-for="(opts, id) in activeModals"
              :key="id">
        <transition appear
                    :name="opts.transition">
            <component v-bind="opts.props"
                       :is="opts.componentName"
                       v-if="!opts.leaving"
                       :ref="`modal-${id}`"></component>
        </transition>
    </template>
</template>

<script>
import { mapState } from 'vuex';

export default {
    data() {
        return {
            hasActiveModals: false,
        };
    },

    computed: {
        ...mapState(['activeModals']),

        activeModalIDs() {
            return Object.keys(this.activeModals)
                .filter((id) => !this.activeModals[id].leaving);
        },
    },

    watch: {
        activeModals: {
            handler() {
                const hasActiveModals = this.activeModalIDs.length > 0;

                // When you go from one modal to another there can be a short delay where there are no
                // modals open, and then you get a flash on the backdrop fading out then back in.
                // To solve this we delay fading out the backdrop with a timeout.
                // If the handler runs again before the timeout is up, we clear the timeout.
                if (!hasActiveModals) {
                    this.clearHasActiveTimeout = setTimeout(() => {
                        this.hasActiveModals = false;
                    }, 10);
                } else {
                    if (this.clearHasActiveTimeout) {
                        clearTimeout(this.clearHasActiveTimeout);
                        this.clearHasActiveTimeout = null;
                    }
                    this.hasActiveModals = true;
                }
            },
            deep: true,
        },
    },

    mounted() {
        this.keydownHandler = (e) => {
            // Dispatch the escape key press to the topmost modal.
            if (e.key === 'Escape') {
                const topModalID = this.activeModalIDs[this.activeModalIDs.length - 1];
                if (
                    topModalID
                    && this.$refs[`modal-${topModalID}`][0].escPressed
                ) {
                    // Call the escPressed method on the modal if it is defined.
                    this.$refs[`modal-${topModalID}`][0].escPressed();
                }
            }
        };
        document.addEventListener('keydown', this.keydownHandler, { passive: true });
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.keydownHandler);
    },
};
</script>

<style lang="less">
@import (reference) "../../../less/variables";

// Using the weird name .modl instead of .modal because .modal is already used by bootstrap
// and it causes other weird things to happen (like being hidden by default).

.modl-backdrop-base {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: @vue-modal-z-index - 10;
    background: rgba(0, 0, 0, 0.5);
}
</style>
